import { Col, Row } from "react-bootstrap";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { Button, Tooltip } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useLoader } from "../../context/LoaderContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ColDef } from "ag-grid-enterprise";
import ExcelIcon from "../../assets/images/ExcelExport.png";
import { MetaDataItemModel } from "../../types/MetaDataItemModel";
import {
  GetMetaDataItems,
  MetaDataItemStatusChange,
} from "../../api/metadataFieldsApi/metadataFieldsApi";
import moment from "moment";
import EditIcon from "@mui/icons-material/Edit";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import BlockIcon from "@mui/icons-material/Block";
import { appSettings } from "../../config/appSetting";
import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import isPagePermission from "../../utils/common";

const MetadataFields: React.FC = () => {
  const { setLoading } = useLoader();
  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaulPageSize = appSettings.agGridDefaulPageSize;
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const [rowData, setRowData] = useState<any[]>([]);
  const gridRef = useRef<AgGridReact>(null);
  const navigation = useNavigate();
  const [gridRecordExist, setGridRecordExist] = useState(false);

  // Load data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Define columns to exclude
  const columnsToExclude = [
    "itemid",
    "createdBy",
    "isSuperAdmin",
    "searchText",
    "firstName",
    "lastName",
    "active",
    "fieldCounts",
    "noOfRecords",
    "currentPage",
    "sortColumn",
    "sortOrder",
    "totalRecords",
    "isexporttpExcel",
    "contextName",
  ];

  //Rename the columns
  const renameColumns = (val: any) => {
    try {
      if (val !== undefined) {
        if (val.toLowerCase() === "itemname") {
          return "Field Name";
        } else if (val.toLowerCase() === "createddate") {
          return "Created On";
        } else if (val.toLowerCase() === "lastmodifieddate") {
          return "Updated On";
        } else if (val.toLowerCase() === "updatedby") {
          return "Updated By";
        } else {
          return val.replace(/&amp;/, "&");
        }
      }
    } catch (error) {
      console.error("Error in renameColumns", error);
    }
  };

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    try {
      const param: MetaDataItemModel = {
        ItemId: 0,
        ItemName: undefined,
        CreatedDate: undefined,
        LastModifiedDate: undefined,
        CreatedBy: undefined,
        IsSuperAdmin: 1,
        SearchText: undefined,
        UpdatedBy: undefined,
        FirstName: undefined,
        LastName: undefined,
        Active: undefined,
        FieldCounts: undefined,
        NoOfRecords: 0,
        CurrentPage: 0,
        SortColumn: undefined,
        SortOrder: undefined,
        TotalRecords: undefined,
        IsexporttpExcel: false,
        ContextName: undefined,
      };
      const response = await GetMetaDataItems(param);
      if (response.metaDataModel.length > 0) {
        setGridRecordExist(true);
        setRowData(response.metaDataModel);
        // Dynamically get column headers from the data
        const dynamicColumns = Object.keys(response.metaDataModel[0])
          .filter((key) => !columnsToExclude.includes(key))
          .map((key: string) => {
            if (
              key.toLocaleLowerCase() === "createddate" ||
              key.toLocaleLowerCase() === "lastmodifieddate"
            ) {
              return {
                headerName: renameColumns(
                  key.charAt(0).toUpperCase() + key.slice(1)
                ),
                field: key,
                sortable: true,
                filter: true,
                enableRowGroup: true,
                width: 200,
                valueFormatter: (params: any) => {
                  return params.value
                    ? moment
                        .utc(params.value)
                        .local()
                        .format("MM-DD-YYYY HH:mm:ss")
                    : "";
                },
              };
            } else if (key.toLocaleLowerCase() === "updatedby") {
              return {
                headerName: renameColumns(
                  key.charAt(0).toUpperCase() + key.slice(1)
                ),
                field: key,
                sortable: true,
                filter: true,
                enableRowGroup: true,
                width: 200,
                flex: 1,
                valueFormatter: (params: any) => {
                  return params.data !== undefined
                    ? params.data.firstName + " " + params.data.lastName
                    : "";
                },
              };
            } else {
              return {
                headerName: renameColumns(
                  key.charAt(0).toUpperCase() + key.slice(1)
                ),
                field: key,
                sortable: true,
                filter: true,
                enableRowGroup: true,
                width: 200,
                flex: 1,
              };
            }
          });

        // Combine static and dynamic column definitions
        const combinedColumnDefs = [
          {
            headerName: "#",
            width: 70,
            suppressSizeToFit: true,
            colId: "colRowNum",
            sortable: false,
            filter: false,
            resizable: false,
            enableRowGroup: false,
            valueGetter: (params: any) => {
              return params.node ? (params.node.rowIndex ?? 0) + 1 : 0;
            },
          },
          ...dynamicColumns,
          {
            field: "Actions",
            headerName: "Actions",
            sortable: false,
            enableRowGroup: false,
            pinned: "right" as const,
            lockPinned: true,
            suppressSizeToFit: true,
            width: 100,
            minWidth: 100,
            cellClass: "actions-button-cell",
            cellRenderer: actionCellRenderer,
            filter: false,
            resizable: false,
            colId: "colAction",
          },
        ];

        setColumnDefs(combinedColumnDefs);
      } else {
        setRowData([]);
        setGridRecordExist(false);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  //#region QuickFilter
  const onFilterTextBoxChanged = useCallback(() => {
    gridRef.current!.api.setGridOption(
      "quickFilterText",
      (document.getElementById("filter-text-box") as HTMLInputElement).value
    );
  }, []);
  //#end filter

  // Action cell renderer
  const actionCellRenderer = (props: any) => {
    try {
      if (props.data === undefined) {
        return "";
      } else {
        const handleEditAction = () => {
          const selectedRowData = props.data;
          navigation("/createNewMetaDataItem", {
            state: { rowData: selectedRowData, action: "Edit" },
          });
        };
        return (
          <>
            {isPagePermission("Allow Edit","MetaDataItem",718) && (
              <Tooltip title="Click to edit the Field">
                <a style={{ color: "var(--aggrid-text-color)" }}>
                  <EditIcon
                    className="icon-button-other"
                    onClick={handleEditAction}
                  />
                </a>
              </Tooltip>
            )}
            <a style={{ color: "var(--aggrid-text-color)" }}>
              {props.data.active ? (
                <Tooltip title="Click to deactivate the Field">
                  <PlayCircleIcon
                    onClick={() => handleDeactivate(props.data.itemid, false)}
                    className="icon-button-other"
                    style={{
                      padding: "2px",
                      color: "var(--aggrid-text-color)",
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Click to activate the Field">
                  <BlockIcon
                    onClick={() => handleDeactivate(props.data.itemid, true)}
                    className="icon-button-other"
                    style={{
                      padding: "2px",
                      color: "var(--aggrid-text-color)",
                    }}
                  />
                </Tooltip>
              )}
            </a>
          </>
        );
      }
    } catch (error) {
      console.error("Error in actionCellRenderer", error);
    }
  };

  // Handel Activate/Deactivate
  const handleDeactivate = async (itemId: number, activate: boolean) => {
    setLoading(true);
    try {
      const param: MetaDataItemModel = {
        ItemId: itemId,
        ItemName: undefined,
        CreatedDate: undefined,
        LastModifiedDate: undefined,
        CreatedBy: undefined,
        IsSuperAdmin: undefined,
        SearchText: undefined,
        UpdatedBy: undefined,
        FirstName: undefined,
        LastName: undefined,
        Active: undefined,
        FieldCounts: undefined,
        NoOfRecords: undefined,
        CurrentPage: undefined,
        SortColumn: undefined,
        SortOrder: undefined,
        TotalRecords: undefined,
        IsexporttpExcel: undefined,
        ContextName: undefined,
      };
      let response = await MetaDataItemStatusChange(param);
      if (response) {
        fetchData();
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Handle Export to excel
  const handleExport = () => {
    try {
      if (gridRef.current) {
        gridRef.current.api.exportDataAsExcel({
          fileName:
            "MetadataFieldsReports_" + moment().format("YYYYMMDD") + ".xlsx",
          sheetName: "MetadataFields Report",
          columnKeys: columnDefs
            .map((colDef) => colDef.field) //NOSONAR
            .filter((field): field is string => typeof field === "string"), // NOSONAR
          processCellCallback: function (params) {
            return params.value;
          },
        });
      }
    } catch (error) {
      console.error("Error in handleExport", error);
    }
  };

  // Handel Create New Metadata Item
  const handelCreateNew = () => {
    navigation("/createNewMetaDataItem");
  };

  // Render component UI
  return (
    <div style={{ overflow: "hidden" }}>
      <NavigationBar menuTitle="Configuration" title="Metadata Management" screenTitle="Metadata Fields" text="Metadata Fields" />
      <Row style={{ marginLeft: "1em" }}>
        <Col xs={12}>
          <div
            className="rp-main-inner col-sm-12"
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            {/* Quick Filter section, Create and Export to excel */}
            <div
              className="col-sm-5"
              style={{
                width: "48%",
                padding: ".5em",
                marginTop: ".1em",
              }}
            >
              <input
                type="text"
                id="filter-text-box"
                placeholder="Quick Filter..."
                data-testid="search-input"
                style={{
                  width: "25em",
                  height: "2.5em",
                  fontSize: "11px",
                  paddingLeft: ".5em",
                  marginBottom: ".1em",
                }}
                onInput={onFilterTextBoxChanged}
              />
            </div>
            <div
              className="col-sm-5"
              style={{
                width: "48%",
                textAlign: "right",
                padding: ".5em",
                marginTop: ".1em",
              }}
            >
              {isPagePermission("Allow Create New","MetaDataItem",716) && (
                <Button
                  variant="contained"
                  size="small"
                  className="button create-new-button"
                  onClick={handelCreateNew}
                >
                  Create New
                </Button>
              )}
              {gridRecordExist && (
                <>
                  {isPagePermission("Allow Export to Excel","MetaDataItem",717) && (
                    <Tooltip title="Click to export the grid content in excel">
                      <button
                        type="button"
                        onClick={handleExport}
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                        aria-label="Export to Excel"
                      >
                        <img
                          src={ExcelIcon}
                          alt="Doc-Distro"
                          className="excel-icon"
                          style={{ marginTop: ".3em" }}
                        />
                      </button>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="rp-main-inner col-sm-12">
            {/* Ag-Grid Section */}
            <div className="rp-main-inner col-sm-12">
              <div
                className="ag-theme-quartz-dark"
                style={{
                  height: "66vh",
                  marginTop: ".1vh",
                  width: "99%",
                }}
              >
                <AgGridReact
                  columnDefs={columnDefs}
                  rowData={rowData}
                  pagination={true}
                  paginationPageSize={defaulPageSize}
                  paginationPageSizeSelector={pageSizeSelector}
                  rowGroupPanelShow="always"
                  pivotPanelShow="always"
                  ref={gridRef}
                  headerHeight={30}
                  loading={!rowData}
                  overlayNoRowsTemplate={
                    '<span class="ag-overlay-loading-center">No rows to show</span>'
                  }
                  loadingOverlayComponent={CustomLoadingOverlay}
                />
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};
export default MetadataFields;
